/**
 * Live Chat tickets live in the same Firebase project as the Driver app.
 * Dev uses `support_tickets_dev`, QA uses `support_tickets_qa`, Enterprise
 * stays on `support_tickets`. Staff presence follows the same lane so a
 * local agent is not online for Enterprise routing.
 */
import { getChatSupportProjectId } from '../config/chatSupportFirebase'

export type SupportTicketsLane = 'prod' | 'qa' | 'dev'

export type SupportSystemModeFile = {
  SYSTEM_MODE?: string
  SYSTEM_MODE_DISPLAY?: string
}

const LEGACY_STAFF_COLLECTION = 'staff_users'

let runtimeSystemMode = ''
let systemModeLoad: Promise<SupportSystemModeFile | null> | null = null

export function primeSupportTicketsLaneFromSystemMode(mode: string): void {
  runtimeSystemMode = String(mode || '')
    .trim()
    .toLowerCase()
}

function hostnameLane(hostname: string): SupportTicketsLane | null {
  const host = hostname.toLowerCase()
  if (!host || host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0' || host.endsWith('.local')) {
    return 'dev'
  }
  if (host.includes('qa')) return 'qa'
  if (host === 'enterprise.valt.africa' || host === 'console.valt.africa' || host.endsWith('.console.valt.africa')) {
    return 'prod'
  }
  return null
}

function laneFromMode(mode: string): SupportTicketsLane | null {
  const value = mode.trim().toLowerCase()
  if (value === 'qa' || value === 'staging') return 'qa'
  if (value === 'dev' || value === 'development') return 'dev'
  if (value === 'production' || value === 'prod') return 'prod'
  return null
}

/**
 * SYSTEM_MODE wins, then hostname. Auth scope cannot pull a dev console
 * or localhost onto the enterprise collection.
 */
export function supportTicketsLane(): SupportTicketsLane {
  const fromRuntime = laneFromMode(runtimeSystemMode)
  if (fromRuntime) return fromRuntime

  if (typeof window !== 'undefined') {
    const fromHost = hostnameLane(window.location.hostname)
    if (fromHost) return fromHost
  }

  const fromEnv = laneFromMode(String(import.meta.env.VITE_SYSTEM_MODE || ''))
  if (fromEnv) return fromEnv

  if (import.meta.env.DEV) return 'dev'

  const scope = String(import.meta.env.VITE_AUTH_SCOPE || '').toLowerCase()
  if (scope === 'staging' || scope === 'qa') return 'qa'
  if (scope === 'production' || scope === 'prod' || scope === 'live') return 'prod'
  return 'prod'
}

/** Fetch system mode once, then resolve the lane. Listeners must await this. */
export function loadSupportSystemMode(): Promise<SupportSystemModeFile | null> {
  if (systemModeLoad) return systemModeLoad
  if (typeof window === 'undefined') {
    systemModeLoad = Promise.resolve(null)
    return systemModeLoad
  }
  systemModeLoad = fetch('/system-mode.json', { cache: 'no-store' })
    .then((r) => (r.ok ? r.json() : null))
    .then((data: SupportSystemModeFile | null) => {
      if (data?.SYSTEM_MODE) primeSupportTicketsLaneFromSystemMode(data.SYSTEM_MODE)
      return data
    })
    .catch(() => null)
  return systemModeLoad
}

export function ensureSupportTicketsLane(): Promise<void> {
  return loadSupportSystemMode().then(() => undefined)
}

/** Opens this Firestore path in the Firebase console. Access still depends on that login. */
export function firebaseConsoleDataUrl(path: string): string {
  const projectId = getChatSupportProjectId()
  const encoded = path
    .split('/')
    .filter(Boolean)
    .map((part) => encodeURIComponent(part))
    .join('~2F')
  return `https://console.firebase.google.com/project/${projectId}/firestore/databases/-default-/data/~2F${encoded}`
}

/** Root Firestore collection + Storage path prefix for this Console. */
export function supportTicketsCollection(): string {
  const lane = supportTicketsLane()
  if (lane === 'qa') return 'support_tickets_qa'
  if (lane === 'dev') return 'support_tickets_dev'
  return 'support_tickets'
}

/** Staff presence for this lane. Each lane has its own collection. */
export function supportStaffCollection(): string {
  const lane = supportTicketsLane()
  if (lane === 'qa') return 'staff_users_qa'
  if (lane === 'dev') return 'staff_users_dev'
  return LEGACY_STAFF_COLLECTION
}

/**
 * Console-started outbound threads (one document per driverId).
 * Separate from inbound support tickets — no round-robin.
 */
export function driverOutboundCollection(): string {
  const lane = supportTicketsLane()
  if (lane === 'qa') return 'driver_outbound_qa'
  if (lane === 'dev') return 'driver_outbound_dev'
  return 'driver_outbound'
}

/** Lane tag written on outbound docs (Flutter filters the same way as tickets). */
export function driverOutboundEnvironment(): string | undefined {
  const lane = supportTicketsLane()
  if (lane === 'dev') return 'DEV'
  if (lane === 'qa') return 'QA'
  return undefined
}

/**
 * Dev, QA, and Enterprise staff documents are one row per console email.
 * Ticket assignment still stores the Firebase Auth uid.
 */
export function staffDocsKeyedByEmail(collectionName?: string): boolean {
  const name = collectionName || supportStaffCollection()
  return name === 'staff_users' || name === 'staff_users_dev' || name === 'staff_users_qa'
}

/**
 * Document id is the console email so the Firebase list is readable.
 * Tickets keep the Firebase Auth uid.
 */
export function supportStaffDocumentId(firebaseUid: string, consoleEmail?: string | null): string {
  if (!staffDocsKeyedByEmail()) return firebaseUid
  const email = String(consoleEmail || '')
    .trim()
    .toLowerCase()
  if (!email || email.includes('/') || email === '.' || email === '..') return firebaseUid
  return email
}

export function legacyStaffCollection(): string {
  return LEGACY_STAFF_COLLECTION
}

/**
 * Name-order pointer for one circle.
 * Dev and QA do not move the Enterprise pointer.
 */
export function supportRibbonDocId(circle: 'admin' | 'onboarding'): string {
  const lane = supportTicketsLane()
  if (lane === 'prod') return `round_robin_${circle}`
  return `round_robin_${circle}_${lane}`
}

/**
 * Flutter dev tickets set `environment: "DEV"`. Enterprise tickets omit it.
 * A dev console drops those so a wrong collection cannot show them.
 */
export function ticketMatchesLane(data: { environment?: string | null }): boolean {
  const lane = supportTicketsLane()
  const env = String(data.environment || '')
    .trim()
    .toUpperCase()
  if (lane === 'dev') return env === 'DEV'
  if (lane === 'qa') {
    if (!env || env === 'QA' || env === 'STAGING') return true
    return false
  }
  if (env === 'DEV' || env === 'QA' || env === 'STAGING') return false
  return true
}
